import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeviceHardwareEmoji, getDeviceStatus, formatLastSeen } from '../../utils/deviceHelpers';
import {
  Box,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Collapse,
  Badge,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

const AdminClientes = () => {
  const { t } = useTranslation();
  const [clientesOriginal, setClientesOriginal] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('habilitado');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedClienteId, setExpandedClienteId] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [dispositivos, setDispositivos] = useState({});
  const [loadingDispositivos, setLoadingDispositivos] = useState({});
  const [oauthIdentities, setOauthIdentities] = useState({});
  const [loadingOauthIdentities, setLoadingOauthIdentities] = useState({});
  const [expandedDispositivoId, setExpandedDispositivoId] = useState(null);
  const navigate = useNavigate();

  const columnsToDisplay = [
    'id',
    'email',
    'nombre',
    'apellidos',
    'telefono',
    'habilitado',
    'acepto_politicas',
    'forma_pago',
  ];

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const token = localStorage.getItem('auth-token');
        
        const response = await fetch(`${baseURL}/api/clientes/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            `Error fetching clientes: ${response.status} - ${
              errorData?.detail || response.statusText
            }`
          );
        }
        const data = await response.json();
        setClientesOriginal(data);
        setClientes(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchClientes();
  }, []);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    if (sortBy) {
      const sortedClientes = [...clientesOriginal].sort((a, b) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortOrder === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortOrder === 'asc' ? valueA - valueB : valueB - a[sortBy];
        } else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
          return sortOrder === 'asc'
            ? (valueA === valueB ? 0 : (valueA ? 1 : -1))
            : (valueA === valueB ? 0 : (valueA ? -1 : 1));
        } else {
          const stringA = String(valueA);
          const stringB = String(valueB);
          return sortOrder === 'asc'
            ? stringA.localeCompare(stringB)
            : stringB.localeCompare(stringA);
        }
      });
      setClientes(sortedClientes);
    } else {
      setClientes(clientesOriginal);
    }
  }, [sortBy, sortOrder, clientesOriginal]);

  const renderCellValue = (cliente, key) => {
    if (key === 'id') {
      // Mostrar el UUID completo en un badge con word-break
      return (
        <Badge className="admin-badge admin-badge-info" colorScheme="blue" fontSize="xs" whiteSpace="normal" wordBreak="break-all">
          {cliente[key]}
        </Badge>
      );
    }
    if (key === 'habilitado' || key === 'acepto_politicas') {
      return cliente[key] ? '✅' : '❌'; // Emoticonos para booleanos
    }
    return cliente[key] !== null && cliente[key] !== undefined
      ? String(cliente[key])
      : 'N/A';
  };

  const fetchDispositivos = async (clienteId) => {
    if (dispositivos[clienteId]) {
      // Ya están cargados, solo expandir/contraer
      setExpandedClienteId(expandedClienteId === clienteId ? null : clienteId);
      return;
    }

    console.log(`[AdminClientes] Consultando dispositivos para cliente: ${clienteId}`);
    setLoadingDispositivos({ ...loadingDispositivos, [clienteId]: true });
    
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const url = `${baseURL}/api/dispositivos/usuario/${clienteId}`;
      console.log(`[AdminClientes] URL: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`[AdminClientes] Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[AdminClientes] Error response: ${errorText}`);
        throw new Error(`Error al obtener dispositivos: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`[AdminClientes] Dispositivos encontrados: ${data.length}`, data);
      
      // Ordenar dispositivos por last_seen (más reciente primero)
      const dispositivosOrdenados = data.sort((a, b) => {
        const dateA = new Date(a.last_seen || 0);
        const dateB = new Date(b.last_seen || 0);
        return dateB - dateA; // Descendente
      });
      
      setDispositivos({ ...dispositivos, [clienteId]: dispositivosOrdenados });
      setExpandedClienteId(clienteId);
    } catch (err) {
      console.error('[AdminClientes] Error al cargar dispositivos:', err);
      setDispositivos({ ...dispositivos, [clienteId]: [] });
      setExpandedClienteId(clienteId);
    } finally {
      setLoadingDispositivos({ ...loadingDispositivos, [clienteId]: false });
    }
  };

  const fetchOauthIdentities = async (clienteId) => {
    setLoadingOauthIdentities(prev => ({ ...prev, [clienteId]: true }));

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      const url = `${baseURL}/api/clientes/${clienteId}/oauth-identities`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error al obtener OAuth identities: ${response.status}`);
      }

      const data = await response.json();
      setOauthIdentities(prev => ({ ...prev, [clienteId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      console.error('[AdminClientes] Error al cargar OAuth identities:', err);
      setOauthIdentities(prev => ({ ...prev, [clienteId]: [] }));
    } finally {
      setLoadingOauthIdentities(prev => ({ ...prev, [clienteId]: false }));
    }
  };

  const getProviderColorScheme = (provider) => {
    const providerName = String(provider || '').toLowerCase();
    if (providerName === 'google') return 'red';
    if (providerName === 'facebook') return 'facebook';
    if (providerName === 'instagram') return 'pink';
    if (providerName === 'tiktok') return 'purple';
    return 'gray';
  };

  const handleToggleSection = async (clienteId, section) => {
    const isSameCliente = expandedClienteId === clienteId;
    const isSameSection = expandedSection === section;

    if (isSameCliente && isSameSection) {
      setExpandedClienteId(null);
      setExpandedSection(null);
      setExpandedDispositivoId(null);
      return;
    }

    setExpandedClienteId(clienteId);
    setExpandedSection(section);
    setExpandedDispositivoId(null);

    if (section === 'devices' && !dispositivos[clienteId]) {
      await fetchDispositivos(clienteId);
      return;
    }

    if (section === 'oauth' && !oauthIdentities[clienteId]) {
      await fetchOauthIdentities(clienteId);
    }
  };

  if (loading) {
    return (
      <Box className="admin-loading">
        <Spinner size="xl" />
        <Text className="admin-loading-text">{t('admin_clientes_cargando')}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert className="admin-alert admin-alert-error" status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="admin-page-container" maxWidth="100%" width="100%">
      <Box className="admin-page-header" flexWrap="wrap" gap={2}>
        <Text className="admin-page-title">
          {t('admin_clientes_titulo')}
        </Text>
        <Button
          className="admin-button-success"
          colorScheme="teal"
          onClick={() => navigate('/admin/alta-cliente')}
        >
          {t('admin_clientes_crear')}
        </Button>
      </Box>

      {clientes.length > 0 ? (
        <Box className="admin-table-container" width="100%">
          <Table className="admin-table" variant="simple" size="sm" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <Thead>
            <Tr>
              <Th width="92px"></Th>
              <Th width="60px" whiteSpace="normal">{t('admin_clientes_acciones')}</Th>
              {columnsToDisplay.map((key) => (
                <Th
                  key={key}
                  cursor="pointer"
                  onClick={() => handleSort(key)}
                  width={
                    key === 'habilitado' ? '70px' :
                    key === 'acepto_politicas' ? '80px' :
                    key === 'forma_pago' ? '80px' :
                    key === 'id' ? '140px' :
                    key === 'email' ? '200px' :
                    key === 'nombre' ? '120px' :
                    key === 'apellidos' ? '120px' :
                    key === 'telefono' ? '100px' :
                    'auto'
                  }
                  whiteSpace={
                    ['habilitado', 'acepto_politicas', 'forma_pago'].includes(key) ? 'normal' : 'nowrap'
                  }
                >
                  {t(`col_${key}`)}
                  {sortBy === key && (sortOrder === 'asc' ? ' ⬆️' : ' ⬇️')}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {clientes.map((cliente) => (
              <React.Fragment key={cliente.id}>
                <Tr
                  _hover={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
                >
                  <Td>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        as="button"
                        type="button"
                        onClick={() => handleToggleSection(cliente.id, 'devices')}
                        fontSize="lg"
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg={expandedClienteId === cliente.id && expandedSection === 'devices' ? 'blue.600' : 'blue.500'}
                        color="white"
                        _hover={{ bg: 'blue.600' }}
                        title="Dispositivos vinculados"
                      >
                        {expandedClienteId === cliente.id && expandedSection === 'devices' ? '🔼' : '🔽'}
                      </Box>

                      <Box
                        as="button"
                        type="button"
                        onClick={() => handleToggleSection(cliente.id, 'oauth')}
                        fontSize="lg"
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg={expandedClienteId === cliente.id && expandedSection === 'oauth' ? 'purple.600' : 'purple.500'}
                        color="white"
                        _hover={{ bg: 'purple.600' }}
                        title="OAuth vinculados"
                      >
                        {expandedClienteId === cliente.id && expandedSection === 'oauth' ? '🔼' : '🔽'}
                      </Box>
                    </Box>
                  </Td>
                  <Td>
                    <IconButton
                      aria-label={t('admin_clientes_editar', { nombre: cliente.nombre, apellidos: cliente.apellidos })}
                      icon={<span aria-hidden="true">✏️</span>}
                      size="sm"
                      onClick={() => {
                        navigate(`/admin/editar-cliente/${cliente.id}`);
                      }}
                    />
                  </Td>
                  {columnsToDisplay.map((key) => (
                    <Td key={`${cliente.id}-${key}`} maxWidth="200px" overflow="hidden" textOverflow="ellipsis" whiteSpace="normal" wordBreak="break-word">
                      {renderCellValue(cliente, key)}
                    </Td>
                  ))}
                </Tr>
                <Tr>
                  <Td colSpan={columnsToDisplay.length + 2} p={0} border="none" maxWidth="100vw">
                    <Collapse in={expandedClienteId === cliente.id} animateOpacity>
                      <Box p={4} bg="gray.800" borderRadius="md" m={2} maxWidth="calc(100vw - 40px)" overflow="hidden">
                        {expandedSection === 'devices' && loadingDispositivos[cliente.id] ? (
                          <Box display="flex" alignItems="center">
                            <Spinner size="sm" mr={2} />
                            <Text>Cargando dispositivos...</Text>
                          </Box>
                        ) : expandedSection === 'devices' && dispositivos[cliente.id] && dispositivos[cliente.id].length > 0 ? (
                          <Box>
                            {dispositivos[cliente.id].map((dispositivo) => (
                              <Box key={dispositivo.id} mb={2}>
                                <Box
                                  p={3}
                                  bg="white"
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor="gray.200"
                                  cursor="pointer"
                                  _hover={{ bg: 'white', borderColor: 'gray.400' }}
                                  onClick={() => setExpandedDispositivoId(
                                    expandedDispositivoId === dispositivo.id ? null : dispositivo.id
                                  )}
                                >
                                  <Box display="flex" alignItems="center" gap={2}>
                                    <Text fontSize="2xl">{getDeviceHardwareEmoji(dispositivo.platform, dispositivo.user_agent)}</Text>
                                    <Text fontSize="lg">{getDeviceStatus(dispositivo.last_seen).emoji}</Text>
                                    <Badge className="admin-badge admin-badge-info" colorScheme="purple" fontSize="sm">
                                      {dispositivo.custom_device_id || dispositivo.id.substring(0, 8) || 'Desconocido'}
                                    </Badge>
                                    <Text fontSize="xs" color="gray.500" ml={1}>
                                      ({formatLastSeen(dispositivo.last_seen)})
                                    </Text>
                                    <Badge className="admin-badge admin-badge-success" colorScheme="green" ml="auto" mr={2}>
                                      {dispositivo.access_count} accesos
                                    </Badge>
                                    <Text fontSize="xl">
                                      {expandedDispositivoId === dispositivo.id ? '🔼' : '🔽'}
                                    </Text>
                                  </Box>
                                </Box>

                                <Collapse in={expandedDispositivoId === dispositivo.id} animateOpacity>
                                  <Box 
                                    mt={2} 
                                    p={0}
                                    borderRadius="md" 
                                    overflow="hidden"
                                  >
                                    <Box
                                      dangerouslySetInnerHTML={{ __html: dispositivo.html_detalle || '<p>No hay detalles disponibles</p>' }}
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: '20px',
                                        backgroundColor: 'transparent',
                                        '& > div': {
                                          display: 'contents'
                                        },
                                        '& > div > div': {
                                          gridColumn: 'auto'
                                        },
                                        '& .info-box': {
                                          margin: 0,
                                          borderRadius: 0
                                        },
                                        '& table': {
                                          width: '100%',
                                          borderCollapse: 'collapse'
                                        },
                                        '& td': {
                                          padding: '8px',
                                          fontSize: '14px'
                                        },
                                        '@media (max-width: 768px)': {
                                          gridTemplateColumns: '1fr'
                                        }
                                      }}
                                    />
                                  </Box>
                                </Collapse>
                              </Box>
                            ))}
                          </Box>
                        ) : expandedSection === 'devices' ? (
                          <Text color="gray.500">No hay dispositivos registrados para este cliente</Text>
                        ) : expandedSection === 'oauth' && loadingOauthIdentities[cliente.id] ? (
                          <Box display="flex" alignItems="center">
                            <Spinner size="sm" mr={2} />
                            <Text>Cargando OAuth vinculados...</Text>
                          </Box>
                        ) : expandedSection === 'oauth' && oauthIdentities[cliente.id] && oauthIdentities[cliente.id].length > 0 ? (
                          <Box>
                            {oauthIdentities[cliente.id].map((identity) => (
                              <Box
                                key={identity.id}
                                p={3}
                                mb={2}
                                bg="white"
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor="gray.200"
                              >
                                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                  <Badge colorScheme={getProviderColorScheme(identity.provider)} className="admin-badge admin-badge-info">
                                    {(identity.provider || 'unknown').toUpperCase()}
                                  </Badge>
                                  <Text fontSize="sm" color="gray.700">
                                    open_id: {identity.open_id || 'N/A'}
                                  </Text>
                                  <Text fontSize="sm" color="gray.700">
                                    union_id: {identity.union_id || 'N/A'}
                                  </Text>
                                </Box>
                                <Box mt={2} display="flex" flexDirection="column" gap={1}>
                                  <Text fontSize="sm" color="gray.700">
                                    email: {identity.email || 'N/A'}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    creado: {identity.created_at ? new Date(identity.created_at).toLocaleString() : 'N/A'}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    último login: {identity.last_login_at ? new Date(identity.last_login_at).toLocaleString() : 'N/A'}
                                  </Text>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Text color="gray.500">No hay OAuth vinculados para este cliente</Text>
                        )}
                      </Box>
                    </Collapse>
                  </Td>
                </Tr>
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
        </Box>
      ) : (
        <Text>{t('admin_clientes_sin_registros')}</Text>
      )}
    </Box>
  );
};

export default AdminClientes;